import React, { useState, useEffect } from 'react';
import { collection, query, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

export default function MentalCapacityAssessmentModal({ serverId, serviceUserId, onClose }) {
    const [assessments, setAssessments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchAssessments = async () => {
            setLoading(true);
            try {
                const ref = collection(db, 'servers', serverId, 'serviceUsers', serviceUserId, 'mentalAssessment');
                const snap = await getDocs(query(ref));
                
                const loaded = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                loaded.sort((a, b) => {
                    const getMs = (val) => {
                        if (!val) return 0;
                        if (typeof val.toDate === 'function') return val.toDate().getTime();
                        if (val.seconds) return val.seconds * 1000;
                        return new Date(val).getTime() || 0;
                    };
                    const dateA = getMs(a.dateOfAssessment || a.createdAt);
                    const dateB = getMs(b.dateOfAssessment || b.createdAt);
                    return dateB - dateA;
                });
                
                setAssessments(loaded);
            } catch (err) {
                console.error("Error fetching mental assessments:", err);
                setError("Failed to load mental capacity assessments.");
            } finally {
                setLoading(false);
            }
        };

        if (serverId && serviceUserId) {
            fetchAssessments();
        }
    }, [serverId, serviceUserId]);

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '20px', color: '#111827' }}>Mental Capacity Assessment</h2>
                        <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>MCA Governance and Records</p>
                    </div>
                    <button onClick={onClose} style={styles.closeBtn}>×</button>
                </div>

                <div style={styles.scrollArea}>
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading assessments...</div>
                    ) : error ? (
                        <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444' }}>{error}</div>
                    ) : assessments.length === 0 ? (
                        <div style={styles.emptyState}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🧠</div>
                            <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No Assessments</h3>
                            <p style={{ margin: 0, color: '#6b7280' }}>No mental capacity assessments found.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {assessments.map((mca, index) => {
                                const isV2 = mca.revisionNumber != null || mca.version === 2 || mca.isCurrent === true;
                                const title = mca.decisionTitle || mca.title || 'Untitled Assessment';
                                
                                return (
                                    <div key={mca.id || index} style={styles.recordCard}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                            <div>
                                                <h3 style={{ margin: 0, fontSize: '16px', color: '#111827' }}>{title}</h3>
                                                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
                                                    Date: {mca.dateOfAssessment ? new Date(mca.dateOfAssessment?.seconds ? mca.dateOfAssessment.seconds * 1000 : mca.dateOfAssessment).toLocaleDateString() : 'N/A'}
                                                </div>
                                            </div>
                                            <span style={{ 
                                                display: 'inline-block', 
                                                padding: '4px 10px', 
                                                borderRadius: '9999px', 
                                                fontSize: '11px', 
                                                fontWeight: 'bold',
                                                backgroundColor: isV2 ? '#dcfce7' : '#f3f4f6',
                                                color: isV2 ? '#166534' : '#6b7280',
                                                border: `1px solid ${isV2 ? '#bbf7d0' : '#d1d5db'}`
                                            }}>
                                                {isV2 ? 'V2 CURRENT' : 'V1 ARCHIVE'}
                                            </span>
                                        </div>
                                        
                                        {/* Display some basic fields generically if available */}
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', borderTop: '1px solid #f3f4f6', paddingTop: '16px' }}>
                                            {mca.decisionMaker && (
                                                <div style={{ flex: '1', minWidth: '150px' }}>
                                                    <label style={styles.fieldLabel}>Decision Maker</label>
                                                    <div style={styles.fieldValue}>{mca.decisionMaker}</div>
                                                </div>
                                            )}
                                            {mca.outcome && (
                                                <div style={{ flex: '1', minWidth: '150px' }}>
                                                    <label style={styles.fieldLabel}>Outcome</label>
                                                    <div style={styles.fieldValue}>{mca.outcome}</div>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div style={{ marginTop: '16px' }}>
                                            <button style={styles.viewBtn} onClick={() => alert('View detailed MCA form: Coming to React V2 soon.')}>
                                                View Full Assessment
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

const styles = {
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
    modalContent: { backgroundColor: 'white', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' },
    modalHeader: { padding: '24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' },
    closeBtn: { background: 'none', border: 'none', fontSize: '24px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1 },
    scrollArea: { padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#f9fafb' },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', textAlign: 'center', backgroundColor: 'white', borderRadius: '8px', border: '1px dashed #d1d5db' },
    recordCard: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
    fieldLabel: { display: 'block', fontSize: '11px', textTransform: 'uppercase', color: '#6b7280', marginBottom: '4px', fontWeight: 'bold' },
    fieldValue: { fontSize: '14px', color: '#111827' },
    viewBtn: { width: '100%', padding: '10px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', color: '#374151', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }
};
